import { useState, useEffect } from 'react';
import { Heart, Brain, Check, X, ArrowRight, Trophy, RotateCcw, Star, Target, Bed, Shield, Sparkles, ArrowLeft, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { motion, AnimatePresence } from 'framer-motion';
import { crystals } from '@/data/crystalData';
import { practicalScenarios, psychologicalSupport } from '@/data/crystalQuizData';
import { supabase } from '@/integrations/supabase/client';
import { useGameSounds } from '@/hooks/useGameSounds';

interface RemedyQuestion {
  type: 'practical' | 'psychological' | 'category';
  question: string;
  crystalId: string;
  correctAnswer: string;
  options: string[];
  explanation: string;
  icon: React.ComponentType<{ className?: string }>;
}

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

const categoryQuestions = [
  { category: 'anxiety', question: 'Which crystal is best for calming anxiety?', icon: Brain },
  { category: 'sleep', question: 'Which crystal promotes restful sleep?', icon: Bed },
  { category: 'love', question: 'Which crystal opens the heart to love?', icon: Heart },
  { category: 'protection', question: 'Which crystal provides protection from negativity?', icon: Shield },
  { category: 'confidence', question: 'Which crystal boosts confidence and personal power?', icon: Target },
  { category: 'abundance', question: 'Which crystal attracts abundance and prosperity?', icon: Sparkles },
];

function generateRemedyQuestions(count: number): RemedyQuestion[] {
  const questions: RemedyQuestion[] = [];
  
  // Mix practical, psychological, and category questions
  const practicalShuffled = shuffleArray([...practicalScenarios]);
  const psychShuffled = shuffleArray([...psychologicalSupport]);
  const categoryShuffled = shuffleArray([...categoryQuestions]);
  
  for (let i = 0; i < count; i++) {
    const questionType = i % 3;
    
    if (questionType === 0 && practicalShuffled.length > 0) {
      const scenario = practicalShuffled.pop()!;
      const crystal = crystals.find(c => c.id === scenario.crystalId);
      if (!crystal) continue;
      
      const wrongOptions = shuffleArray(
        crystals.filter(c => c.id !== scenario.crystalId)
      ).slice(0, 3).map(c => c.name);
      
      questions.push({
        type: 'practical',
        question: scenario.scenario,
        crystalId: scenario.crystalId,
        correctAnswer: crystal.name,
        options: shuffleArray([crystal.name, ...wrongOptions]),
        explanation: scenario.explanation,
        icon: Target
      });
    } else if (questionType === 1 && psychShuffled.length > 0) {
      const condition = psychShuffled.pop()!;
      const crystal = crystals.find(c => c.id === condition.crystalId);
      if (!crystal) continue;
      
      const wrongOptions = shuffleArray(
        crystals.filter(c => c.id !== condition.crystalId)
      ).slice(0, 3).map(c => c.name);
      
      questions.push({
        type: 'psychological',
        question: `Which crystal helps with ${condition.condition}?`,
        crystalId: condition.crystalId,
        correctAnswer: crystal.name,
        options: shuffleArray([crystal.name, ...wrongOptions]),
        explanation: condition.explanation,
        icon: Brain
      });
    } else if (categoryShuffled.length > 0) {
      const catQ = categoryShuffled.pop()!;
      const categoryCrystals = crystals.filter(c => c.categories.includes(catQ.category));
      if (categoryCrystals.length === 0) continue;
      
      const correctCrystal = categoryCrystals[0];
      const wrongOptions = shuffleArray(
        crystals.filter(c => !c.categories.includes(catQ.category))
      ).slice(0, 3).map(c => c.name);
      
      questions.push({
        type: 'category',
        question: catQ.question,
        crystalId: correctCrystal.id,
        correctAnswer: correctCrystal.name,
        options: shuffleArray([correctCrystal.name, ...wrongOptions]),
        explanation: correctCrystal.categoryReasons?.[catQ.category] || correctCrystal.description,
        icon: catQ.icon
      });
    }
  }
  
  return shuffleArray(questions);
}

interface CrystalRemedyMatcherProps {
  onBack?: () => void;
}

export function CrystalRemedyMatcher({ onBack }: CrystalRemedyMatcherProps) {
  const [gameState, setGameState] = useState<'menu' | 'playing' | 'result'>('menu');
  const [questions, setQuestions] = useState<RemedyQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [xpEarned, setXpEarned] = useState(0);
  const [streak, setStreak] = useState(0);
  const { playSound, preloadSounds, soundsEnabled, toggleSounds } = useGameSounds();

  useEffect(() => {
    preloadSounds(['correct', 'incorrect', 'levelComplete', 'streak']);
  }, [preloadSounds]);

  const startGame = (count: number) => {
    setQuestions(generateRemedyQuestions(count));
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setXpEarned(0);
    setStreak(0);
    setGameState('playing');
  };

  const handleAnswer = async (answer: string) => {
    if (selectedAnswer) return;
    
    setSelectedAnswer(answer);
    const current = questions[currentIndex];
    const isCorrect = answer === current.correctAnswer;

    const baseXp = current.type === 'psychological' ? 20 : 15;
    if (isCorrect) {
      const streakBonus = streak >= 3 ? 5 : streak >= 2 ? 3 : 0;
      setScore(score + 1);
      setXpEarned(xpEarned + baseXp + streakBonus);
      setStreak(streak + 1);
      
      if (streak >= 2) {
        playSound('streak');
      } else {
        playSound('correct');
      }
    } else {
      setStreak(0);
      playSound('incorrect');
    }

    setShowResult(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const crystal = crystals.find(c => c.id === current.crystalId);
        await supabase.from('crystal_quiz_answers').insert({
          user_id: user.id,
          crystal_name: crystal?.name || '',
          question_type: current.type,
          question: current.question,
          user_answer: answer,
          correct_answer: current.correctAnswer,
          is_correct: isCorrect,
          xp_awarded: isCorrect ? baseXp : 0
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const nextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      finishGame();
    }
  };

  const finishGame = async () => {
    setGameState('result');
    playSound('levelComplete');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: existing } = await supabase
        .from('crystal_learning_progress')
        .select('*')
        .eq('user_id', user.id)
        .single();

      const today = new Date().toISOString().split('T')[0];
      const newXp = (existing?.xp_earned || 0) + xpEarned;

      if (existing) {
        await supabase.from('crystal_learning_progress').update({
          total_quizzes_completed: existing.total_quizzes_completed + 1,
          correct_answers: existing.correct_answers + score,
          xp_earned: newXp,
          last_quiz_date: today
        }).eq('user_id', user.id);
      } else {
        await supabase.from('crystal_learning_progress').insert({
          user_id: user.id,
          total_quizzes_completed: 1,
          correct_answers: score,
          xp_earned: xpEarned,
          last_quiz_date: today
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const current = questions[currentIndex];
  const currentCrystal = current ? crystals.find(c => c.id === current.crystalId) : null;

  return (
    <div className="space-y-6">
      <AnimatePresence mode="wait">
        {gameState === 'menu' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
              <CardHeader className="text-center pb-2">
                <CardTitle className="flex items-center justify-center gap-2">
                  <Heart className="h-5 w-5 text-primary" />
                  Crystal Remedies
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Learn which crystals to use for different situations and wellbeing needs
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {onBack && (
                  <Button onClick={onBack} variant="ghost" className="w-full mb-2">
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back to Learning Hub
                  </Button>
                )}
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="p-2 bg-background/50 rounded-lg">
                    <Target className="h-4 w-4 mx-auto text-primary mb-1" />
                    <span className="text-muted-foreground">Practical</span>
                  </div>
                  <div className="p-2 bg-background/50 rounded-lg">
                    <Brain className="h-4 w-4 mx-auto text-accent mb-1" />
                    <span className="text-muted-foreground">Psychological</span>
                  </div>
                  <div className="p-2 bg-background/50 rounded-lg">
                    <Heart className="h-4 w-4 mx-auto text-secondary mb-1" />
                    <span className="text-muted-foreground">Wellbeing</span>
                  </div>
                </div>
                <Button onClick={() => startGame(6)} className="w-full" size="lg">
                  Quick Round (6 scenarios)
                </Button>
                <Button onClick={() => startGame(12)} variant="outline" className="w-full">
                  Full Round (12 scenarios)
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {gameState === 'playing' && current && (
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between text-sm">
              <Badge variant="outline" className="flex items-center gap-1">
                {current.type === 'practical' && <Target className="h-3 w-3" />}
                {current.type === 'psychological' && <Brain className="h-3 w-3" />}
                {current.type === 'category' && <Heart className="h-3 w-3" />}
                {current.type === 'practical' ? 'Situation' : current.type === 'psychological' ? 'Wellbeing' : 'Category'}
              </Badge>
              <div className="flex items-center gap-2">
                {streak >= 2 && <span className="text-secondary">🔥 {streak} streak</span>}
                <span className="text-primary font-medium">+{xpEarned} XP</span>
              </div>
            </div>
            <Progress value={((currentIndex + 1) / questions.length) * 100} className="h-2" />

            {/* Question */}
            <Card className="bg-gradient-to-br from-muted/50 to-background">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    {(() => {
                      const Icon = current.icon;
                      return <Icon className="h-5 w-5 text-primary" />;
                    })()}
                  </div>
                  <p className="text-lg font-medium text-foreground leading-relaxed">
                    {current.question}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Options */}
            <div className="grid grid-cols-2 gap-3">
              {current.options.map((option, i) => {
                const isCorrect = option === current.correctAnswer;
                const isSelected = option === selectedAnswer;
                const optionCrystal = crystals.find(c => c.name === option);
                
                let className = 'h-auto py-3 flex-col gap-2';
                if (showResult) {
                  if (isCorrect) className += ' bg-accent/20 border-accent';
                  else if (isSelected) className += ' bg-destructive/20 border-destructive';
                }

                return (
                  <Button
                    key={i}
                    variant="outline"
                    className={className}
                    onClick={() => handleAnswer(option)}
                    disabled={!!selectedAnswer}
                  >
                    {optionCrystal && (
                      <img 
                        src={optionCrystal.image} 
                        alt={option}
                        className="w-10 h-10 object-contain"
                      />
                    )}
                    <span className="text-sm font-medium">{option}</span>
                    {showResult && isCorrect && <Check className="h-4 w-4 text-accent" />}
                    {showResult && isSelected && !isCorrect && <X className="h-4 w-4 text-destructive" />}
                  </Button>
                );
              })}
            </div>

            {/* Feedback */}
            <AnimatePresence>
              {showResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className={`border-2 ${
                    selectedAnswer === current.correctAnswer 
                      ? 'bg-accent/10 border-accent/30' 
                      : 'bg-destructive/5 border-destructive/20'
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        {currentCrystal && (
                          <img 
                            src={currentCrystal.image} 
                            alt={currentCrystal.name}
                            className="w-12 h-12 object-contain"
                          />
                        )}
                        <div className="flex-1">
                          <p className="font-medium">
                            {selectedAnswer === current.correctAnswer 
                              ? `Correct! +${(current.type === 'psychological' ? 20 : 15) + (streak >= 3 ? 5 : streak >= 2 ? 3 : 0)} XP`
                              : `The answer is ${current.correctAnswer}`}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {current.explanation}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Button onClick={nextQuestion} className="w-full mt-4">
                    {currentIndex < questions.length - 1 ? (
                      <>Next Scenario <ArrowRight className="h-4 w-4 ml-2" /></>
                    ) : (
                      <>See Results <Trophy className="h-4 w-4 ml-2" /></>
                    )}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {gameState === 'result' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <Card className="bg-gradient-to-br from-primary/20 to-accent/10 border-primary/30">
              <CardHeader className="text-center">
                <div className="mx-auto p-4 rounded-full bg-primary/20 w-fit mb-4">
                  <Trophy className="h-10 w-10 text-primary" />
                </div>
                <CardTitle className="text-2xl">
                  {score === questions.length ? 'Crystal Healer!' : 'Well Done!'}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-background/50 p-4">
                    <p className="text-3xl font-bold text-accent">{score}/{questions.length}</p>
                    <p className="text-sm text-muted-foreground">Correct</p>
                  </Card>
                  <Card className="bg-background/50 p-4">
                    <p className="text-3xl font-bold text-primary">+{xpEarned}</p>
                    <p className="text-sm text-muted-foreground">XP Earned</p>
                  </Card>
                </div>

                <Progress value={(score / questions.length) * 100} className="h-3" />
                <p className="text-lg font-semibold">{Math.round((score / questions.length) * 100)}% Accuracy</p>

                {score === questions.length && (
                  <div className="flex items-center justify-center gap-2 text-accent">
                    <Star className="h-5 w-5 fill-current" />
                    <span>Remedy Master!</span>
                    <Star className="h-5 w-5 fill-current" />
                  </div>
                )}

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setGameState('menu')} className="flex-1">
                    <RotateCcw className="h-4 w-4 mr-2" /> Menu
                  </Button>
                  <Button onClick={() => startGame(6)} className="flex-1">
                    <Heart className="h-4 w-4 mr-2" /> Again
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
